import { useEffect, useState } from 'react';
import api, { BACKEND } from '@/lib/api';
import { Trophy, Twitter, TrendingUp, TrendingDown, Download } from 'lucide-react';

function tweetIntent(win, traderName, streak) {
  const winSym = win.winning_coin?.symbol || '';
  const loseSym = win.losing_coin?.symbol || '';
  const payout = Math.round(win.payout_usdt || 0);
  const streakLine = streak >= 3 ? ` 🔥 ${streak}-battle streak.` : '';
  const text = `Called it. ${winSym} beat ${loseSym} in this week's Elite Coin Battle on @BITGONODE.${streakLine} Payout: ${payout} USDT (1.5× pool bonus).\n\nJoin & pick the next winner →`;
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
}

function pngUrl(winId) {
  const base = BACKEND;
  return `${base}/api/battles/win_card/${winId}/image.png`;
}

export default function EliteWinsCard() {
  const [wins, setWins] = useState([]);
  const [streak, setStreak] = useState(0);
  const [name, setName] = useState('');
  const [state, setState] = useState('loading');

  useEffect(() => {
    Promise.all([
      api.get('/battles/my_elite_wins?limit=6').catch(() => ({ data: { wins: [] } })),
      api.get('/battles/multiplier/mine').catch(() => ({ data: {} })),
      api.get('/auth/me').catch(() => ({ data: {} })),
    ]).then(([w, m, me]) => {
      setWins(w.data.wins || []);
      setStreak(m.data.user_streak || 0);
      setName(me.data.name || '');
      setState('ready');
    });
  }, []);

  if (state === 'loading') return null;
  if (!wins.length) return null;

  return (
    <div className="mt-10" data-testid="elite-wins-card">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-5 h-5 text-[#F6465D]" />
        <h2 className="text-white text-xl font-semibold">Your Elite Wins</h2>
        <span className="text-xs uppercase tracking-wider text-[#848E9C]">brag on X · earn respect</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {wins.map(w => {
          const wc = w.winning_coin || {};
          const lc = w.losing_coin || {};
          const wUp = (wc.change_24h ?? 0) >= 0;
          return (
            <div key={w.win_id} className="border border-[#F6465D]/30 bg-gradient-to-br from-[#F6465D]/8 via-[#181A20] to-[#181A20] p-4" data-testid={`elite-win-${w.win_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#F6465D] font-mono">Elite · Week of {w.week_start?.slice(0, 10)}</div>
                <div className="text-[10px] font-mono text-[#5E6673]">payout ×{w.pool_bonus_mult}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {wc.image && <img src={wc.image} alt="" className="w-9 h-9" />}
                  <div className="min-w-0">
                    <div className="text-white font-semibold text-sm">{wc.symbol}</div>
                    <div className={`text-[10px] font-mono flex items-center gap-0.5 ${wUp ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                      {wUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {wUp ? '+' : ''}{wc.change_24h?.toFixed(2)}%
                    </div>
                  </div>
                </div>
                <div className="text-[#F6465D] font-display font-bold text-sm">beat</div>
                <div className="flex items-center gap-2 flex-1 min-w-0 justify-end opacity-60">
                  <div className="min-w-0 text-right">
                    <div className="text-[#EAECEF] text-sm">{lc.symbol}</div>
                  </div>
                  {lc.image && <img src={lc.image} alt="" className="w-7 h-7" />}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="border border-[#2B3139] bg-[#0B0E11] px-3 py-1.5">
                  <div className="text-[10px] uppercase text-[#848E9C]">Stake</div>
                  <div className="font-mono text-white text-sm">{(w.stake_usdt || 0).toFixed(0)} USDT</div>
                </div>
                <div className="border border-[#F6465D]/30 bg-[#F6465D]/10 px-3 py-1.5">
                  <div className="text-[10px] uppercase text-[#F6465D]">Payout</div>
                  <div className="font-mono text-[#F6465D] text-sm">+{(w.payout_usdt || 0).toFixed(0)} USDT</div>
                </div>
              </div>
              <a
                href={tweetIntent(w, name, streak)}
                target="_blank" rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2 border border-[#1DA1F2]/40 bg-[#1DA1F2]/10 text-[#1DA1F2] text-sm font-semibold rounded-sm hover:bg-[#1DA1F2]/20"
                data-testid={`share-win-${w.win_id}`}
              >
                <Twitter className="w-4 h-4" /> Share proof-of-win on X
              </a>
              <a
                href={pngUrl(w.win_id)}
                download={`cryptox_win_${w.win_id}.png`}
                target="_blank" rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 w-full py-2 border border-[#2B3139] bg-[#0B0E11] text-[#B7BDC6] text-sm font-semibold rounded-sm hover:text-white hover:border-[#FCD535]/50"
                data-testid={`download-png-${w.win_id}`}
              >
                <Download className="w-4 h-4" /> Download PNG · attach to your tweet
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}
